import { Project, CallExpression, Node, ProjectOptions } from 'ts-morph';

interface FieldInfo {
  name: string;
  columnName: string;
  type: string;
  length?: number;
  precision?: number;
  scale?: number;
  nullable: boolean;
  hasDefault: boolean;
  defaultValue?: string;
  notNull: boolean;
  mode?: 'string' | 'number' | 'bigint' | 'boolean' | 'date';
  isPrimaryKey: boolean;
  isUnique: boolean;
  isArray: boolean;
  comment?: string;
  enumValues?: string[];
  withTimezone?: boolean;
  references?: {
    table: string;
    column: string;
  };
}

interface TableInfo {
  tableName: string;
  variableName: string;
  fields: FieldInfo[];
}

class DrizzleSchemaParser {
  private project: Project;

  constructor(projectOptions: ProjectOptions) {
    this.project = new Project(projectOptions);
  }

  parseSchemaFile(filePath: string): TableInfo[] {
    const sourceFile = this.project.addSourceFileAtPath(filePath);
    const tables: TableInfo[] = [];

    // 查找所有变量声明
    const variableStatements = sourceFile.getVariableStatements();

    for (const statement of variableStatements) {
      const declarations = statement.getDeclarations();

      for (const declaration of declarations) {
        const initializer = declaration.getInitializer();

        if (initializer && Node.isCallExpression(initializer)) {
          const expression = initializer.getExpression();

          // 检查是否是 pgTable 调用
          if (expression.getText() === 'pgTable') {
            const tableInfo = this.parsePgTable(
              declaration.getName(),
              initializer,
            );
            if (tableInfo) {
              tables.push(tableInfo);
            }
          }
        }
      }
    }

    return tables;
  }

  private parsePgTable(
    variableName: string,
    callExpr: CallExpression,
  ): TableInfo | null {
    const args = callExpr.getArguments();

    if (args.length < 2) {
      return null;
    }

    // 表名
    const tableName = args[0].getText().replace(/['"]/g, '');

    // 字段定义对象
    const fieldsArg = args[1];

    if (!Node.isObjectLiteralExpression(fieldsArg)) {
      return null;
    }

    const fields: FieldInfo[] = [];

    // 解析字段
    const properties = fieldsArg.getProperties();

    for (const prop of properties) {
      if (Node.isPropertyAssignment(prop)) {
        const fieldName = prop.getName();
        const initializer = prop.getInitializer();

        // 获取字段上方的注释
        const leadingComments = prop.getLeadingCommentRanges();
        let comment: string | undefined;

        if (leadingComments.length > 0) {
          comment = leadingComments
            .map((c) => c.getText())
            .join('\n')
            .replace(/\/\//g, '')
            .trim();
        }

        if (initializer && Node.isCallExpression(initializer)) {
          const fieldInfo = this.parseField(fieldName, initializer, comment);
          fields.push(fieldInfo);
        }
      }
    }

    return {
      tableName,
      variableName,
      fields,
    };
  }

  private parseField(
    fieldName: string,
    callExpr: CallExpression,
    comment?: string,
  ): FieldInfo {
    const fieldInfo: FieldInfo = {
      name: fieldName,
      columnName: fieldName,
      type: '',
      nullable: true,
      hasDefault: false,
      notNull: false,
      isPrimaryKey: false,
      isUnique: false,
      isArray: false,
      comment,
    };

    // 获取字段类型和基础参数
    this.parseBaseType(callExpr, fieldInfo);

    // 解析整个调用链
    this.parseCallChain(callExpr, fieldInfo);

    return fieldInfo;
  }

  private parseBaseType(callExpr: CallExpression, fieldInfo: FieldInfo): void {
    // 找到最底层的类型调用
    let current: Node = callExpr;
    let baseCall: CallExpression | null = null;

    while (Node.isCallExpression(current)) {
      baseCall = current;
      const expression = current.getExpression();

      if (Node.isPropertyAccessExpression(expression)) {
        current = expression.getExpression();
      } else {
        break;
      }
    }

    if (!baseCall) {
      return;
    }

    const expression = baseCall.getExpression();
    let typeName = '';

    if (Node.isPropertyAccessExpression(expression)) {
      typeName = expression.getName();
    } else {
      typeName = expression.getText();
    }

    fieldInfo.type = typeName;

    // 解析类型参数
    const args = baseCall.getArguments();

    if (args.length > 0) {
      const firstArg = args[0];

      // 字符串参数 - 列名
      if (Node.isStringLiteral(firstArg)) {
        fieldInfo.columnName = firstArg.getLiteralText();
      }
      // 对象参数 - 配置
      else if (Node.isObjectLiteralExpression(firstArg)) {
        this.parseTypeConfig(firstArg, fieldInfo);
      }
      // 数组参数 - enum 值
      else if (Node.isArrayLiteralExpression(firstArg)) {
        fieldInfo.enumValues = firstArg
          .getElements()
          .map((el) => el.getText().replace(/['"]/g, ''));
      }
    }

    // 第二个参数可能是配置对象
    if (args.length > 1 && Node.isObjectLiteralExpression(args[1])) {
      this.parseTypeConfig(args[1], fieldInfo);
    }
  }

  private parseTypeConfig(objLiteral: Node, fieldInfo: FieldInfo): void {
    if (!Node.isObjectLiteralExpression(objLiteral)) {
      return;
    }

    const properties = objLiteral.getProperties();

    for (const prop of properties) {
      if (Node.isPropertyAssignment(prop)) {
        const propName = prop.getName();
        const value = prop.getInitializer()?.getText();

        switch (propName) {
          case 'length':
            fieldInfo.length = value ? parseInt(value) : undefined;
            break;
          case 'precision':
            fieldInfo.precision = value ? parseInt(value) : undefined;
            break;
          case 'scale':
            fieldInfo.scale = value ? parseInt(value) : undefined;
            break;
          case 'default':
            fieldInfo.hasDefault = true;
            fieldInfo.defaultValue = value;
            break;
          // 时间精度（用于 timestamp, time 等）
          case 'withTimezone':
            fieldInfo.withTimezone = value === 'true';
            break;
          case 'mode':
            // mode 可以是 'string' | 'number' | 'bigint' | 'boolean' | 'date'
            // 用于指定 JS 中的数据类型
            fieldInfo.mode = value?.replace(/['"]/g, '') as
              | 'string'
              | 'number'
              | 'bigint'
              | 'boolean'
              | 'date'
              | undefined;
            break;
          default:
            throw new Error(`Unsupported property: ${propName}`);
        }
      }
    }
  }

  private parseCallChain(callExpr: CallExpression, fieldInfo: FieldInfo): void {
    let current: Node = callExpr;

    while (Node.isCallExpression(current)) {
      const expression = current.getExpression();

      if (Node.isPropertyAccessExpression(expression)) {
        const methodName = expression.getName();
        const args = current.getArguments();

        switch (methodName) {
          case 'notNull':
            fieldInfo.notNull = true;
            fieldInfo.nullable = false;
            break;

          case 'default':
            fieldInfo.hasDefault = true;
            if (args.length > 0) {
              fieldInfo.defaultValue = args[0].getText();
            }
            break;

          case 'defaultRandom':
            fieldInfo.hasDefault = true;
            fieldInfo.defaultValue = 'random';
            break;

          case 'primaryKey':
            fieldInfo.isPrimaryKey = true;
            fieldInfo.notNull = true;
            fieldInfo.nullable = false;
            break;

          case 'unique':
            fieldInfo.isUnique = true;
            break;

          case 'array':
            fieldInfo.isArray = true;
            break;

          case 'references':
            if (args.length > 0) {
              const refArg = args[0].getText();
              // 解析引用，如 () => users.id
              const match = refArg.match(/=>\s*(\w+)\.(\w+)/);
              if (match) {
                fieldInfo.references = {
                  table: match[1],
                  column: match[2],
                };
              }
            }
            break;
          default:
            throw new Error(`Unsupported method: ${methodName}`);
        }

        current = expression.getExpression();
      } else {
        break;
      }
    }
  }
}

export { DrizzleSchemaParser, TableInfo, FieldInfo };

